import { useEffect, useRef, useCallback, useContext } from 'react';
import { HotkeyContext, ControllerButton, GAMEPAD_BUTTON_MAP } from '../lib/hotkey-context';

type SimpleButtonHandler = () => void;
interface ComplexButtonHandler {
  holdable: boolean;
  pressed: () => void;
  released: () => void;
}
type ButtonHandler = SimpleButtonHandler | ComplexButtonHandler;

export interface ButtonHandlers {
  [key: string]: ButtonHandler;
}

// Track button states
interface ButtonStates {
  [key: number]: boolean;
}

// Helper to determine if handler is complex
const isComplexHandler = (handler: ButtonHandler): handler is ComplexButtonHandler => {
  return typeof handler === 'object' && 'pressed' in handler && 'released' in handler;
};



// Singleton manager for gamepad navigation
class GamepadManager {
  private static instance: GamepadManager;
  private subscribers: Set<(gamepad: Gamepad, previousButtonStates: ButtonStates, eventType: 'press' | 'hold' | 'repeat') => void> = new Set();
  private intervalId?: number;
  private previousButtonStates: ButtonStates = {};
  private buttonPressStartTime: { [key: number]: number } = {};  // Track when button press started
  private buttonHasTriggered: { [key: number]: boolean } = {};   // Track if initial press was handled
  private readonly LONG_PRESS_MS = 1000;    // Time before repeat starts
  private readonly REPEAT_INTERVAL_MS = 100; // Repeat interval when held

  private constructor() {
    this.startPolling();
  }

  static getInstance(): GamepadManager {
    if (!this.instance) {
      this.instance = new GamepadManager();
    }
    return this.instance;
  }

  subscribe(callback: (gamepad: Gamepad, previousButtonStates: ButtonStates, eventType: 'press' | 'hold' | 'repeat') => void) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  private startPolling() {
    this.intervalId = window.setInterval(() => {
      const gamepads = navigator.getGamepads();
      for (const gamepad of gamepads) {
        if (gamepad) {
          const now = Date.now();
          const buttonEvents: { state: boolean; type: 'press' | 'hold' | 'repeat' }[] = [];

          // Process each button independently
          gamepad.buttons.forEach((button, index) => {
            const wasPressed = this.previousButtonStates[index];
            const isPressed = button.pressed;

            // Only process state changes or repeats for currently pressed buttons
            if (isPressed && !wasPressed) {
              buttonEvents[index] = { state: true, type: 'press' };
              this.buttonPressStartTime[index] = now;
              this.buttonHasTriggered[index] = true;
            } else if (isPressed && wasPressed) {
              const holdTime = now - (this.buttonPressStartTime[index] || 0);
              
              // Don't reset buttonHasTriggered while the button is still held
              if (holdTime >= this.LONG_PRESS_MS) {
                if ((holdTime - this.LONG_PRESS_MS) % this.REPEAT_INTERVAL_MS < 5) {
                  buttonEvents[index] = { state: true, type: 'repeat' };
                }
              } else if (!this.buttonHasTriggered[index]) {
                buttonEvents[index] = { state: true, type: 'hold' };
              }
            } else if (!isPressed && wasPressed) {
              // Only reset states when the button is actually released
              buttonEvents[index] = { state: false, type: 'press' };
              delete this.buttonPressStartTime[index];
              this.buttonHasTriggered[index] = false;
            }

            // Update previous state for this button
            this.previousButtonStates[index] = isPressed;
          });

          // Notify subscribers of events
          this.subscribers.forEach(callback => 
            buttonEvents.forEach((event, index) => {
              if (event) {
                callback(gamepad, { [index]: event.state }, event.type);
              }
            })
          );
        }
      }
    }, 5);
  }

  cleanup() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}

export function useGamepadNavigation(customButtonHandlers?: ButtonHandlers) {
  const focusableElements = useRef<HTMLElement[]>([]);
  const currentFocusIndex = useRef<number>(0);
  const { mode } = useContext(HotkeyContext);

  const handleGamepadInput = useCallback((
    gamepad: Gamepad, 
    buttonStates: ButtonStates, 
    eventType: 'press' | 'hold' | 'repeat'
  ) => {
    if (!gamepad || mode !== 'controller') return;

    // Handle button events based on type
    const handleButton = (buttonIndex: number, handler?: ButtonHandler & { holdable?: boolean }) => {
      if (!handler) return;

      const isPressed = buttonStates[buttonIndex];
      
      if (isComplexHandler(handler)) {
        if (isPressed) {
          handler.pressed();
        } else {
          handler.released();
        }
        return;
      }

      // For simple handlers
      if (eventType === 'press' && isPressed) {
        // Case 1: Normal press and release
        handler();
      } else if (eventType === 'hold' && handler.holdable && isPressed) {
        // Case 2: Immediate and continuous hold response
        handler();
      } else if (eventType === 'repeat' && isPressed) {
        // Case 3: Long-press repeat
        handler();
      }
    };

    // Process all buttons dynamically
    gamepad.buttons.forEach((_, index) => {
      const controllerButton = GAMEPAD_BUTTON_MAP[index];
      if (controllerButton && customButtonHandlers?.[controllerButton]) {
        handleButton(index, customButtonHandlers[controllerButton]);
      }
    });

  }, [customButtonHandlers, mode]);

  useEffect(() => {
    const handleGamepadConnected = (e: GamepadEvent) => {
      console.log('Gamepad connected:', e.gamepad);
    };

    window.addEventListener('gamepadconnected', handleGamepadConnected);
    
    const unsubscribe = GamepadManager.getInstance().subscribe(handleGamepadInput);

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      unsubscribe();
    };
  }, [handleGamepadInput]);

  const registerFocusableElements = useCallback((elements: HTMLElement[]) => {
    focusableElements.current = elements;
  }, []);

  return { registerFocusableElements };
}
